import React, { useState, useRef, useMemo, useEffect } from 'react';
import { 
  Pill, 
  Syringe, 
  Flame,
  Activity, 
  Search, 
  Filter, 
  Layers, 
  Plus, 
  Sparkles,
  CheckCircle2,
  PauseCircle,
  ArrowUpDown,
  Download,
  Table,
  Image as ImageIcon,
  RotateCcw,
  Trash2,
  ChevronDown
} from 'lucide-react';
import ProtocolCard from './ProtocolCard';
import SeedDataBanner from './SeedDataBanner';
import ExportOptionsModal from './ExportOptionsModal';
import ExportImageOptionsModal from './ExportImageOptionsModal';
import ImagePreviewModal from './ImagePreviewModal';
import ExportTableModal from './ExportTableModal';
import ExportImageContainer from './ExportImageContainer';
import ClearStackModal from './ClearStackModal';
import CheckpointsModal from './CheckpointsModal';
import { exportStackToCsv } from '../utils/export';
import { 
  filterProtocolsForExport, 
  buildImageFilename, 
  captureElementAsPng 
} from '../utils/exportImage';
import { sortProtocols, normalizeCompoundCategory } from '../utils/sortUtils';
import { DEFAULT_EXPORT_CATEGORIES } from '../utils/exportFilterOptions';
import { 
  HEALTH_CATEGORIES, 
  getEffectivePrimaryCategory, 
  getEffectiveSecondaryCategory, 
  getCategoryConfig 
} from '../utils/healthCategories';
import { useAuth } from '../context/AuthContext';
import { getStoredGroupBy, saveStoredGroupBy, getSortedGroupByOptions } from '../utils/groupPreferences';
import { TIME_OF_DAY_GROUPS, getTimeOfDayGroupKey } from '../utils/calendarUtils';

export default function Dashboard({ 
  currentUser: propCurrentUser,
  protocols, 
  loading, 
  onEditProtocol, 
  onDuplicateProtocol,
  onStatusChange, 
  onDeleteProtocol, 
  onViewHistory,
  onOpenAddModal,
  onSeedData,
  checkpoints = [],
  onClearAll,
  onRestoreCheckpoint,
  onCreateCheckpoint,
  onDeleteCheckpoint
}) {
  const authContext = useAuth ? useAuth() : null;
  const effectiveUser = propCurrentUser || authContext?.currentUser;
  const userId = effectiveUser?.uid;

  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All'); // 'All' | 'Supplement' | 'Peptide' | 'Hormones & AAS'
  const [activeHealthCategory, setActiveHealthCategory] = useState('All'); // 'All' | Health category
  const [activeStatus, setActiveStatus] = useState('Active'); // 'Active' | 'Paused' | 'Completed' | 'All'
  const [sortOption, setSortOption] = useState('default'); // 'default' | 'healthCategory' | 'alphabetical' | 'startDate' | 'endDate'
  const [groupBy, setGroupBy] = useState(() => getStoredGroupBy(userId)); // 'none' | 'category' | 'healthCategory' | 'timeOfDay'
  const [collapsedGroups, setCollapsedGroups] = useState({});

  const groupByOptions = useMemo(() => getSortedGroupByOptions(), []);

  useEffect(() => {
    if (userId) {
      const saved = getStoredGroupBy(userId);
      setGroupBy(saved);
    }
  }, [userId]);

  const handleGroupByChange = (e) => {
    const value = e.target.value;
    setGroupBy(value);
    saveStoredGroupBy(userId, value);
  };

  const toggleGroupCollapse = (groupKey) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }));
  };

  // Text/Markdown Table Export Modal state
  const [isExportTableModalOpen, setIsExportTableModalOpen] = useState(false);

  // CSV Export Modal state
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [exportOptions, setExportOptions] = useState({
    status: 'Active',
    categories: [...DEFAULT_EXPORT_CATEGORIES],
    sortOption: 'default'
  });

  // Image Export Modal states
  const [isExportImageModalOpen, setIsExportImageModalOpen] = useState(false);
  const [exportImageOptions, setExportImageOptions] = useState({
    status: 'Active',
    categories: [...DEFAULT_EXPORT_CATEGORIES],
    sortOption: 'default',
    obfuscateSupplier: false
  });

  // Image Preview Modal states
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [imageDataUrl, setImageDataUrl] = useState('');
  const [previewTitle, setPreviewTitle] = useState('Protocol Stack Image');
  const [previewFilename, setPreviewFilename] = useState('protocol-stack.png');
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);

  // Hidden Render Target state
  const [exportContainerProtocols, setExportContainerProtocols] = useState([]);
  const [exportContainerScope, setExportContainerScope] = useState('Protocol Stack');
  const [exportContainerOptions, setExportContainerOptions] = useState({});
  const exportContainerRef = useRef(null);

  // Checkpoints & Clear Stack state
  const [isClearModalOpen, setIsClearModalOpen] = useState(false);
  const [isCheckpointsModalOpen, setIsCheckpointsModalOpen] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [isSeeding, setIsSeeding] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);

  const uniqueCheckpoints = useMemo(() => {
    const seen = new Set();
    return (checkpoints || []).filter(cp => {
      if (!cp?.id || seen.has(cp.id)) return false;
      seen.add(cp.id);
      return true;
    });
  }, [checkpoints]);
  const [isSeedBannerDismissed, setIsSeedBannerDismissed] = useState(() => {
    return localStorage.getItem('supps_peps_seed_banner_dismissed') === 'true';
  });

  const handleDismissSeedBanner = () => {
    setIsSeedBannerDismissed(true);
    localStorage.setItem('supps_peps_seed_banner_dismissed', 'true');
  };

  const handleConfirmClearAll = async () => {
    if (!onClearAll || isClearing) return;
    setIsClearing(true);
    try {
      await onClearAll();
      setIsClearModalOpen(false);
    } catch (err) {
      console.error("Clear stack failed:", err);
    } finally {
      setIsClearing(false);
    }
  };

  const handleSeedClick = async () => {
    if (!onSeedData || isSeeding) return;
    setIsSeeding(true);
    try {
      await onSeedData();
    } catch (err) {
      console.error("Seed stack failed:", err);
    } finally {
      setIsSeeding(false);
    }
  };

  const handleRestoreClick = async (checkpoint) => {
    if (!onRestoreCheckpoint || isRestoring || !checkpoint) return;
    setIsRestoring(true);
    try {
      await onRestoreCheckpoint(checkpoint);
    } catch (err) {
      console.error("Restore checkpoint failed:", err);
    } finally {
      setIsRestoring(false);
    }
  };

  // Metric Computations
  const activeSupplementsCount = protocols.filter(
    p => p.category === 'Supplement' && p.cycleStatus === 'Active'
  ).length;

  const activePeptidesCount = protocols.filter(
    p => p.category === 'Peptide' && p.cycleStatus === 'Active'
  ).length;

  const activeHormonesCount = protocols.filter(
    p => normalizeCompoundCategory(p.category) === 'Hormones & AAS' && p.cycleStatus === 'Active'
  ).length;

  const totalActiveCycles = protocols.filter(
    p => p.cycleStatus === 'Active'
  ).length;

  // CSV Export filter helpers
  const getExportCount = (options = {}) => {
    return filterProtocolsForExport(protocols, options).length;
  };

  const handleExportCsv = (options) => {
    exportStackToCsv(protocols, options);
  };

  // Image Export Handler from Overall View Options Modal
  const handleGenerateImageFromOptions = (options) => {
    const filtered = filterProtocolsForExport(protocols, options);
    const filename = buildImageFilename(options);
    const title = `${options.status !== 'All' ? options.status : ''} ${options.category !== 'All Compounds' ? options.category : 'Protocol'} Stack`.trim();

    setExportContainerProtocols(filtered);
    setExportContainerScope(title);
    setExportContainerOptions(options);
    setPreviewTitle(`${title} Image`);
    setPreviewFilename(filename);

    setIsExportImageModalOpen(false); // Dismiss options modal
    setIsPreviewModalOpen(true);     // Open second stacked preview modal
    setIsGeneratingImage(true);
    setImageDataUrl('');

    // Brief delay to allow hidden render node to mount in DOM
    setTimeout(async () => {
      try {
        if (exportContainerRef.current) {
          const url = await captureElementAsPng(exportContainerRef.current);
          setImageDataUrl(url);
        }
      } catch (err) {
        console.error('Failed to generate stack image:', err);
      } finally {
        setIsGeneratingImage(false);
      }
    }, 250);
  };

  // Image Export Handler from Single Card Ellipsis Menu
  const handleExportSingleCardImage = (protocol) => {
    const filename = buildImageFilename({}, protocol);
    const title = `${protocol.name} Protocol`;

    setExportContainerProtocols([protocol]);
    setExportContainerScope(title);
    setExportContainerOptions({});
    setPreviewTitle(`${protocol.name} Protocol Image`);
    setPreviewFilename(filename);

    setIsPreviewModalOpen(true);
    setIsGeneratingImage(true);
    setImageDataUrl('');

    setTimeout(async () => {
      try {
        if (exportContainerRef.current) {
          const url = await captureElementAsPng(exportContainerRef.current);
          setImageDataUrl(url);
        }
      } catch (err) {
        console.error('Failed to generate card image:', err);
      } finally {
        setIsGeneratingImage(false);
      }
    }, 250);
  };

  // Filter Protocols
  const filteredProtocols = protocols.filter(p => {
    const primCat = getEffectivePrimaryCategory(p);
    const secCat = getEffectiveSecondaryCategory(p);

    const matchesSearch = p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (p.brandOrSupplier && p.brandOrSupplier.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (p.intendedOutcome && p.intendedOutcome.toLowerCase().includes(searchQuery.toLowerCase())) ||
      primCat.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (secCat && secCat.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchesCategory =
      activeCategory === 'All' ||
      normalizeCompoundCategory(p.category) === normalizeCompoundCategory(activeCategory);

    const matchesHealthCategory =
      activeHealthCategory === 'All' ||
      primCat === activeHealthCategory ||
      secCat === activeHealthCategory;

    const matchesStatus = activeStatus === 'All' || p.cycleStatus === activeStatus;

    return matchesSearch && matchesCategory && matchesHealthCategory && matchesStatus;
  });

  // Sort Protocols
  const sortedProtocols = sortProtocols(filteredProtocols, sortOption);

  // Group Protocols (optional visual segmentation by Health Category or Compound Type)
  const groupedProtocols = useMemo(() => {
    if (groupBy === 'none') return null;

    if (groupBy === 'healthCategory') {
      const groups = {};
      HEALTH_CATEGORIES.forEach(cat => {
        groups[cat] = [];
      });
      sortedProtocols.forEach(p => {
        const cat = getEffectivePrimaryCategory(p);
        if (!groups[cat]) groups[cat] = [];
        groups[cat].push(p);
      });
      return Object.entries(groups)
        .filter(([_, items]) => items.length > 0)
        .map(([categoryName, items]) => ({
          key: categoryName,
          title: categoryName,
          config: getCategoryConfig(categoryName),
          protocols: items
        }));
    }

    if (groupBy === 'category') {
      const groups = {
        'Peptide': [],
        'Hormones & AAS': [],
        'Supplement': []
      };
      sortedProtocols.forEach(p => {
        const cat = normalizeCompoundCategory(p.category) || 'Supplement';
        if (!groups[cat]) groups[cat] = [];
        groups[cat].push(p);
      });

      const typeConfig = {
        'Peptide': {
          dotClass: 'bg-purple-400',
          badgeClass: 'bg-purple-500/15 text-purple-300 border border-purple-500/30',
          description: 'Peptide and bioregulator research protocols'
        },
        'Hormones & AAS': {
          dotClass: 'bg-rose-500',
          badgeClass: 'bg-rose-500/15 text-rose-300 border border-rose-500/30',
          description: 'Hormone replacement therapy (HRT) and androgen protocols'
        },
        'Supplement': {
          dotClass: 'bg-sky-400',
          badgeClass: 'bg-sky-500/15 text-sky-300 border border-sky-500/30',
          description: 'Dietary supplements, nootropics, and vitamins'
        }
      };

      return Object.entries(groups)
        .filter(([_, items]) => items.length > 0)
        .map(([catName, items]) => ({
          key: catName,
          title: catName === 'Peptide' ? 'Peptides' : catName === 'Supplement' ? 'Supplements' : 'Hormones & AAS',
          config: typeConfig[catName],
          protocols: items
        }));
    }

    if (groupBy === 'timeOfDay') {
      const groups = {
        AM: [],
        NOON: [],
        BEDTIME: [],
        MULTI: [],
        PRN: [],
        UNSPECIFIED: []
      };

      sortedProtocols.forEach(p => {
        const groupKey = getTimeOfDayGroupKey(p.timingOfDay);
        if (groups[groupKey]) {
          groups[groupKey].push(p);
        } else {
          groups.UNSPECIFIED.push(p);
        }
      });

      return TIME_OF_DAY_GROUPS
        .filter(g => groups[g.key].length > 0)
        .map(g => ({
          key: g.key,
          title: g.title,
          config: g.config,
          protocols: groups[g.key]
        }));
    }

    return null;
  }, [sortedProtocols, groupBy]);

  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8 space-y-8">
      
      {/* Hidden Offscreen DOM Render Container for Non-Transparent Image Export */}
      {(isGeneratingImage || isPreviewModalOpen) && (
        <ExportImageContainer
          ref={exportContainerRef}
          protocols={exportContainerProtocols}
          titleScope={exportContainerScope}
          options={exportContainerOptions}
          obfuscateSupplier={Boolean(exportContainerOptions?.obfuscateSupplier)}
        />
      )}

      {/* Metrics Banner */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        
        {/* Total Active Protocols Card (Active Protocol Stack) */}
        <button
          type="button"
          onClick={() => {
            setActiveCategory('All');
            setActiveStatus('Active');
          }}
          className={`glass-card p-5 rounded-2xl flex items-center justify-between border-l-4 border-l-emerald-500 text-left transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] cursor-pointer ${
            activeCategory === 'All' && activeStatus === 'Active'
              ? 'ring-2 ring-emerald-500/50 bg-emerald-500/10 shadow-lg shadow-emerald-500/10'
              : 'hover:border-slate-700'
          }`}
          title="Filter view to Active Protocol Stack"
        >
          <div>
            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Active Stack</div>
            <div className="text-3xl font-extrabold text-white mt-1 font-mono">{totalActiveCycles}</div>
          </div>
          <div className="p-3 rounded-2xl bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
            <Activity className="w-6 h-6" />
          </div>
        </button>

        {/* Active Supps Card */}
        <button
          type="button"
          onClick={() => {
            setActiveCategory('Supplement');
            setActiveStatus('Active');
          }}
          className={`glass-card p-5 rounded-2xl flex items-center justify-between border-l-4 border-l-sky-500 text-left transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] cursor-pointer ${
            activeCategory === 'Supplement' && activeStatus === 'Active'
              ? 'ring-2 ring-sky-500/50 bg-sky-500/10 shadow-lg shadow-sky-500/10'
              : 'hover:border-slate-700'
          }`}
          title="Filter view to Active Supplements"
        >
          <div>
            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Active Supps</div>
            <div className="text-3xl font-extrabold text-white mt-1 font-mono">{activeSupplementsCount}</div>
          </div>
          <div className="p-3 rounded-2xl bg-sky-500/10 text-sky-400 border border-sky-500/20">
            <Pill className="w-6 h-6" />
          </div>
        </button>

        {/* Active Peptides Card */}
        <button
          type="button"
          onClick={() => {
            setActiveCategory('Peptide');
            setActiveStatus('Active');
          }}
          className={`glass-card p-5 rounded-2xl flex items-center justify-between border-l-4 border-l-purple-500 text-left transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] cursor-pointer ${
            activeCategory === 'Peptide' && activeStatus === 'Active'
              ? 'ring-2 ring-purple-500/50 bg-purple-500/10 shadow-lg shadow-purple-500/10'
              : 'hover:border-slate-700'
          }`}
          title="Filter view to Active Peptides"
        >
          <div>
            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Active Peptides</div>
            <div className="text-3xl font-extrabold text-white mt-1 font-mono">{activePeptidesCount}</div>
          </div>
          <div className="p-3 rounded-2xl bg-purple-500/10 text-purple-400 border border-purple-500/20">
            <Syringe className="w-6 h-6" />
          </div>
        </button>

        {/* Active Hormones & AAS Card */}
        <button
          type="button"
          onClick={() => {
            setActiveCategory('Hormones & AAS');
            setActiveStatus('Active');
          }}
          className={`glass-card p-5 rounded-2xl flex items-center justify-between border-l-4 border-l-rose-500 text-left transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] cursor-pointer ${
            (activeCategory === 'Hormones & AAS' || activeCategory === 'Hormone') && activeStatus === 'Active'
              ? 'ring-2 ring-rose-500/50 bg-rose-500/10 shadow-lg shadow-rose-500/10'
              : 'hover:border-slate-700'
          }`}
          title="Filter view to Active Hormones & AAS"
        >
          <div>
            <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Active Hormones & AAS</div>
            <div className="text-3xl font-extrabold text-white mt-1 font-mono">{activeHormonesCount}</div>
          </div>
          <div className="p-3 rounded-2xl bg-rose-500/10 text-rose-400 border border-rose-500/20">
            <Flame className="w-6 h-6" />
          </div>
        </button>

      </div>

      {/* Show Seed Data Banner if protocols array is empty and not dismissed */}
      {!loading && protocols.length === 0 && !isSeedBannerDismissed && (
        <SeedDataBanner 
          onSeed={handleSeedClick} 
          onDismiss={handleDismissSeedBanner}
          loading={loading || isSeeding} 
        />
      )}

      {/* Checkpoint Recovery Prompt when stack is empty */}
      {!loading && protocols.length === 0 && checkpoints && checkpoints.length > 0 && (
        <div className="p-4 rounded-2xl bg-gradient-to-r from-indigo-950/60 to-slate-900 border border-indigo-500/30 mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shadow-xl">
          <div className="flex items-center space-x-3 text-xs text-indigo-200">
            <div className="p-2.5 rounded-xl bg-indigo-500/20 text-indigo-400 border border-indigo-500/30 shrink-0">
              <RotateCcw className="w-5 h-5" />
            </div>
            <div>
              <span className="font-bold text-white text-sm block">Previous Protocol Stack Saved</span>
              <span className="text-slate-400">
                You have a recovery checkpoint ({checkpoints[0].protocolCount || checkpoints[0].protocols?.length || 0} protocols) from {new Date(checkpoints[0].timestamp).toLocaleDateString()}.
              </span>
            </div>
          </div>
          <div className="flex items-center space-x-2 shrink-0">
            <button
              type="button"
              onClick={() => handleRestoreClick(uniqueCheckpoints[0])}
              disabled={isRestoring}
              className="px-4 py-2 rounded-xl bg-gradient-to-r from-indigo-500 to-sky-600 hover:from-indigo-400 hover:to-sky-500 text-white font-bold text-xs shadow-md transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isRestoring ? 'Restoring...' : 'Restore Previous Stack'}
            </button>
            <button
              type="button"
              onClick={() => setIsCheckpointsModalOpen(true)}
              className="px-3 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 font-semibold text-xs border border-slate-700 transition"
            >
              All Checkpoints ({uniqueCheckpoints.length})
            </button>
          </div>
        </div>
      )}

      {/* Search & Filter Toolbar */}
      <div className="glass-panel p-4 rounded-2xl border border-slate-800 space-y-3.5">
        
        {/* Row 1: Search Input & Export Actions */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
          {/* Search Input */}
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-slate-500 absolute left-3.5 top-3" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search protocols, brands, intended outcomes..."
              className="w-full pl-10 pr-4 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs text-white placeholder-slate-500 focus:outline-none focus:border-sky-500 transition"
            />
          </div>

          {/* Export & Stack Actions Toolbar Group */}
          <div className="flex items-center shrink-0 flex-wrap sm:flex-nowrap gap-2">
            {/* Checkpoints Button */}
            <button
              type="button"
              onClick={() => setIsCheckpointsModalOpen(true)}
              className="flex-1 sm:flex-initial inline-flex items-center justify-center space-x-1.5 whitespace-nowrap px-3 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-slate-200 text-xs font-semibold transition border border-slate-700 shadow-sm"
              title="View stack checkpoints & restore history"
            >
              <RotateCcw className="w-3.5 h-3.5 text-indigo-400" />
              <span>Checkpoints</span>
              {uniqueCheckpoints && uniqueCheckpoints.length > 0 && (
                <span className="px-1.5 py-0.2 rounded-full text-[10px] font-mono font-bold bg-indigo-500/20 text-indigo-300 border border-indigo-500/30">
                  {uniqueCheckpoints.length}
                </span>
              )}
            </button>

            {/* Clear Stack Button */}
            {protocols && protocols.length > 0 && (
              <button
                type="button"
                onClick={() => setIsClearModalOpen(true)}
                className="flex-1 sm:flex-initial inline-flex items-center justify-center space-x-1.5 whitespace-nowrap px-3 py-2 rounded-xl bg-rose-950/40 hover:bg-rose-900/60 text-rose-300 hover:text-rose-200 text-xs font-semibold transition border border-rose-800/60 shadow-sm"
                title="Clear all protocols (automatic recovery checkpoint saved)"
              >
                <Trash2 className="w-3.5 h-3.5 text-rose-400" />
                <span className="hidden sm:inline">Clear Stack</span>
                <span className="sm:hidden">Clear</span>
              </button>
            )}

            {/* Export Table Button */}
            <button
              onClick={() => setIsExportTableModalOpen(true)}
              disabled={!protocols || protocols.length === 0}
              className="flex-1 sm:flex-initial inline-flex items-center justify-center space-x-2 whitespace-nowrap px-3 py-2 rounded-xl bg-sky-950/80 hover:bg-sky-900 disabled:opacity-40 disabled:cursor-not-allowed text-sky-200 text-xs font-semibold transition border border-sky-800/80 shadow-sm"
              title="Export stack as Markdown, Plain Text, or Discord Chat"
            >
              <Table className="w-3.5 h-3.5 text-sky-400" />
              <span>Export Table</span>
            </button>

            {/* Export CSV Button */}
            <button
              onClick={() => setIsExportModalOpen(true)}
              disabled={!protocols || protocols.length === 0}
              className="flex-1 sm:flex-initial inline-flex items-center justify-center space-x-2 whitespace-nowrap px-3 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed text-slate-200 text-xs font-semibold transition border border-slate-700"
              title="Export stack as CSV"
            >
              <Download className="w-3.5 h-3.5" />
              <span>Export CSV</span>
            </button>

            {/* Export Image Button */}
            <button
              onClick={() => setIsExportImageModalOpen(true)}
              disabled={!protocols || protocols.length === 0}
              className="flex-1 sm:flex-initial inline-flex items-center justify-center space-x-2 whitespace-nowrap px-3 py-2 rounded-xl bg-purple-950/80 hover:bg-purple-900 disabled:opacity-40 disabled:cursor-not-allowed text-purple-200 text-xs font-semibold transition border border-purple-800/80 shadow-sm"
              title="Export stack as Image"
            >
              <ImageIcon className="w-3.5 h-3.5 text-purple-400" />
              <span>Export Image</span>
            </button>
          </div>
        </div>

        {/* Subtle Divider */}
        <div className="border-t border-slate-800/60"></div>

        {/* Row 2: Category & Status Sliders (opposite sides on desktop) */}
        <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
          
          {/* Category Tabs */}
          <div className="flex flex-wrap sm:flex-nowrap rounded-xl bg-slate-950 p-1 border border-slate-800 gap-1">
            {['All', 'Supplement', 'Peptide', 'Hormones & AAS'].map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`flex-1 sm:flex-none px-3 py-1.5 text-xs font-semibold rounded-lg transition whitespace-nowrap text-center ${
                  activeCategory === cat || (cat === 'Hormones & AAS' && activeCategory === 'Hormone')
                    ? 'bg-sky-600 text-white shadow-sm' 
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                {cat === 'All' ? 'All Compounds' : cat === 'Hormones & AAS' ? 'Hormones & AAS' : `${cat}s`}
              </button>
            ))}
          </div>

          {/* Status Filter */}
          <div className="flex flex-wrap sm:flex-nowrap items-center rounded-xl bg-slate-950 p-1 border border-slate-800 gap-1">
            {['Active', 'Pending', 'Paused', 'Completed', 'All'].map((st) => (
              <button
                key={st}
                onClick={() => setActiveStatus(st)}
                className={`flex-1 sm:flex-none px-3 py-1.5 text-xs font-semibold rounded-lg transition whitespace-nowrap text-center ${
                  activeStatus === st
                    ? 'bg-slate-800 text-sky-400 shadow-sm'
                    : 'text-slate-400 hover:text-white'
                }`}
              >
                {st}
              </button>
            ))}
          </div>
        </div>

        {/* Subtle Divider */}
        <div className="border-t border-slate-800/60"></div>

        {/* Row 3: Health Goal, Grouping & Sort Dropdowns (shared centered row) */}
        <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center justify-center gap-2.5">
          
          {/* Health Target Category Filter */}
          <div className="relative w-full sm:w-auto">
            <Activity className="w-3.5 h-3.5 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <select
              value={activeHealthCategory}
              onChange={(e) => setActiveHealthCategory(e.target.value)}
              className="w-full sm:w-auto sm:min-w-[200px] pl-9 pr-4 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs text-white focus:outline-none focus:border-sky-500 transition cursor-pointer"
              title="Filter by Health Target Category"
            >
              <option value="All">All Health Goals</option>
              {HEALTH_CATEGORIES.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          {/* Group By Selector */}
          <div className="relative w-full sm:w-auto">
            <Layers className="w-3.5 h-3.5 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <select
              value={groupBy}
              onChange={handleGroupByChange}
              className="w-full sm:w-auto sm:min-w-[200px] pl-9 pr-4 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs text-white focus:outline-none focus:border-sky-500 transition cursor-pointer"
              title="Group protocol cards"
            >
              {groupByOptions.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Sort Dropdown */}
          <div className="relative w-full sm:w-auto">
            <ArrowUpDown className="w-3.5 h-3.5 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <select
              value={sortOption}
              onChange={(e) => setSortOption(e.target.value)}
              className="w-full sm:w-auto sm:min-w-[200px] pl-9 pr-4 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs text-white focus:outline-none focus:border-sky-500 transition cursor-pointer"
              title="Sort protocols"
            >
              <option value="default">Sort: Default</option>
              <option value="healthCategory">Sort: Health Category</option>
              <option value="alphabetical">Sort: A–Z</option>
              <option value="startDate">Sort: Start Date</option>
              <option value="endDate">Sort: End Date</option>
            </select>
          </div>
        </div>

      </div>

      {/* Export Table Modal */}
      <ExportTableModal
        isOpen={isExportTableModalOpen}
        onClose={() => setIsExportTableModalOpen(false)}
        protocols={protocols}
        currentSortOption={sortOption}
      />

      {/* Export CSV Options Modal */}
      <ExportOptionsModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        onExport={handleExportCsv}
        onOptionsChange={setExportOptions}
        protocolCount={getExportCount(exportOptions)}
        currentSortOption={sortOption}
      />

      {/* Export Image Options Modal */}
      <ExportImageOptionsModal
        isOpen={isExportImageModalOpen}
        onClose={() => setIsExportImageModalOpen(false)}
        onGenerate={handleGenerateImageFromOptions}
        onOptionsChange={setExportImageOptions}
        protocolCount={getExportCount(exportImageOptions)}
        currentSortOption={sortOption}
      />

      {/* Second Stacked Image Preview Modal */}
      <ImagePreviewModal
        isOpen={isPreviewModalOpen}
        onClose={() => setIsPreviewModalOpen(false)}
        imageDataUrl={imageDataUrl}
        filename={previewFilename}
        title={previewTitle}
        loading={isGeneratingImage}
      />

      {/* Protocol Cards Grid */}
      {loading ? (
        <div className="py-20 text-center text-slate-500 text-xs flex items-center justify-center space-x-2">
          <Activity className="w-5 h-5 animate-spin text-sky-400" />
          <span>Loading protocol data...</span>
        </div>
      ) : filteredProtocols.length === 0 ? (
        <div className="py-16 text-center glass-card rounded-2xl p-8 border border-slate-800">
          <Layers className="w-10 h-10 text-slate-600 mx-auto mb-3" />
          <h4 className="text-base font-bold text-white mb-1">No Protocols Found</h4>
          <p className="text-xs text-slate-400 max-w-sm mx-auto mb-4">
            No matching compounds found for your search or filter selection.
          </p>
          <button
            onClick={onOpenAddModal}
            className="inline-flex items-center space-x-2 px-4 py-2 rounded-xl bg-sky-600 hover:bg-sky-500 text-white text-xs font-semibold transition shadow-md shadow-sky-600/20"
          >
            <Plus className="w-4 h-4" />
            <span>Add Protocol Compound</span>
          </button>

          {protocols.length === 0 && (
            <div className="flex flex-wrap items-center justify-center gap-2.5 mt-4 pt-4 border-t border-slate-800/80">
              <button
                type="button"
                onClick={handleSeedClick}
                disabled={isSeeding}
                className="inline-flex items-center space-x-1.5 px-3.5 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-emerald-400 text-xs font-semibold border border-slate-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Sparkles className="w-3.5 h-3.5" />
                <span>{isSeeding ? 'Loading...' : 'Load Sample Template'}</span>
              </button>
              {uniqueCheckpoints && uniqueCheckpoints.length > 0 && (
                <button
                  type="button"
                  onClick={() => setIsCheckpointsModalOpen(true)}
                  className="inline-flex items-center space-x-1.5 px-3.5 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-indigo-400 text-xs font-semibold border border-slate-700 transition"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  <span>Restore From Checkpoint ({uniqueCheckpoints.length})</span>
                </button>
              )}
            </div>
          )}
        </div>
      ) : groupedProtocols ? (
        <div className="space-y-10">
          <div className="flex items-center justify-between text-xs text-slate-400 pb-1">
            <span className="font-medium text-slate-300">
              {groupBy === 'category'
                ? 'Grouped by Compound Type'
                : groupBy === 'timeOfDay'
                ? 'Grouped by Time of Day'
                : 'Grouped by Health Target Goal'} ({groupedProtocols.length} {groupedProtocols.length === 1 ? 'section' : 'sections'})
            </span>
            <div className="flex items-center space-x-2">
              <button
                type="button"
                onClick={() => setCollapsedGroups({})}
                className="hover:text-sky-400 text-[11px] text-slate-400 transition"
              >
                Expand all
              </button>
              <span className="text-slate-700">•</span>
              <button
                type="button"
                onClick={() => {
                  const allCollapsed = {};
                  groupedProtocols.forEach(g => { allCollapsed[g.key] = true; });
                  setCollapsedGroups(allCollapsed);
                }}
                className="hover:text-sky-400 text-[11px] text-slate-400 transition"
              >
                Collapse all
              </button>
            </div>
          </div>

          {groupedProtocols.map((group) => {
            const isCollapsed = Boolean(collapsedGroups[group.key]);
            return (
              <div key={group.key} className="space-y-4">
                <button
                  type="button"
                  onClick={() => toggleGroupCollapse(group.key)}
                  className="w-full flex items-center justify-between pb-2.5 border-b border-slate-800/80 group/header text-left hover:border-slate-700 transition cursor-pointer select-none"
                  aria-expanded={!isCollapsed}
                >
                  <div className="flex items-center space-x-3">
                    {group.config?.dotClass ? (
                      <span className={`w-2.5 h-2.5 rounded-full ${group.config.dotClass}`} />
                    ) : (
                      <span className="w-2.5 h-2.5 rounded-full bg-sky-400" />
                    )}
                    <h3 className="text-base sm:text-lg font-bold text-white tracking-wide group-hover/header:text-sky-300 transition">
                      {group.title}
                    </h3>
                    <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-slate-800 text-slate-300 border border-slate-700">
                      {group.protocols.length}
                    </span>
                    {group.config?.description && (
                      <span className="hidden md:inline-block text-xs text-slate-500 font-normal">
                        — {group.config.description}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center space-x-1.5 text-xs text-slate-400 group-hover/header:text-slate-200 transition">
                    <span className="text-[11px] font-medium hidden sm:inline">
                      {isCollapsed ? 'Expand' : 'Collapse'}
                    </span>
                    <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isCollapsed ? '-rotate-90 text-slate-500' : 'rotate-0 text-slate-400'}`} />
                  </div>
                </button>

                {!isCollapsed && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-fadeIn">
                    {group.protocols.map((protocol) => (
                      <ProtocolCard
                        key={protocol.id}
                        protocol={protocol}
                        onEdit={onEditProtocol}
                        onDuplicate={onDuplicateProtocol}
                        onStatusChange={onStatusChange}
                        onDelete={onDeleteProtocol}
                        onViewHistory={onViewHistory}
                        onExportImage={handleExportSingleCardImage}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sortedProtocols.map((protocol) => (
            <ProtocolCard
              key={protocol.id}
              protocol={protocol}
              onEdit={onEditProtocol}
              onDuplicate={onDuplicateProtocol}
              onStatusChange={onStatusChange}
              onDelete={onDeleteProtocol}
              onViewHistory={onViewHistory}
              onExportImage={handleExportSingleCardImage}
            />
          ))}
        </div>
      )}

      {/* Clear Stack Modal */}
      <ClearStackModal
        isOpen={isClearModalOpen}
        onClose={() => setIsClearModalOpen(false)}
        onConfirm={handleConfirmClearAll}
        protocolCount={protocols ? protocols.length : 0}
        isClearing={isClearing}
      />

      {/* Checkpoints & Restore Modal */}
      <CheckpointsModal
        isOpen={isCheckpointsModalOpen}
        onClose={() => setIsCheckpointsModalOpen(false)}
        checkpoints={uniqueCheckpoints}
        onRestore={handleRestoreClick}
        onCreateCheckpoint={onCreateCheckpoint}
        onDeleteCheckpoint={onDeleteCheckpoint}
        currentProtocolCount={protocols ? protocols.length : 0}
      />

    </div>
  );
}
